import { useState } from 'react';
import { LuSparkles } from 'react-icons/lu';

import { Switch } from '@/components/ui/switch';

import { PortfolioBoostLearnMore } from '../components/PortfolioBoostLearnMore';
import { MANAGEMENT_FLAGS, TAXABLE_FOOTNOTE } from '../management-flags';
import { ManagementSettings, OnboardingStepProps } from '../types';

export const ManagementStep = ({ state, setState }: OnboardingStepProps) => {
    const management = state.management;
    const portfolioBoostEnabled = state.portfolioBoostEnabled;
    const setFlag = (key: keyof ManagementSettings, value: boolean) =>
        setState((prev) => ({ ...prev, management: { ...prev.management, [key]: value } }));
    const setPortfolioBoost = (value: boolean) => setState((prev) => ({ ...prev, portfolioBoostEnabled: value }));
    const [showLearnMore, setShowLearnMore] = useState(false);

    return (
        <div className="flex max-w-3xl flex-col gap-6">
            <div>
                <h3 className="text-cocoa-1000 text-2xl font-medium">Management</h3>
                <p className="text-cocoa-600 mt-1 text-sm">
                    Determine how the portfolio engine should manage this account.
                </p>
            </div>

            <div className="border-gold-200/60 bg-gold-50/40 flex items-start gap-4 rounded-lg border p-4">
                <div className="from-gold-300 via-gold-400 to-gold-600 flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br text-white">
                    <LuSparkles className="h-5 w-5" />
                </div>
                <div className="min-w-0 flex-1">
                    <h4 className="text-cocoa-1000 text-sm font-semibold">Portfolio Boost</h4>
                    <p className="text-cocoa-600 mt-1 text-sm leading-relaxed">
                        Claro&apos;s Portfolio Boost makes your client accounts tax-smarter, cash-efficient, and
                        automatically optimized — without changing a single investment decision.
                    </p>
                    <button
                        type="button"
                        aria-expanded={showLearnMore}
                        onClick={() => setShowLearnMore((prev) => !prev)}
                        className="text-gold-600 hover:text-gold-500 mt-2 text-xs font-medium underline-offset-2 hover:underline"
                    >
                        {showLearnMore ? 'Show less' : 'Learn more →'}
                    </button>
                    {showLearnMore && (
                        <div className="mt-4">
                            <PortfolioBoostLearnMore />
                        </div>
                    )}
                </div>
            </div>

            <div className="border-cocoa-200 flex items-center justify-between rounded-lg border px-4 py-3">
                <div className="flex items-center gap-2 text-sm">
                    <span
                        className={`h-2 w-2 rounded-full ${portfolioBoostEnabled ? 'bg-emerald-500' : 'bg-cocoa-300'}`}
                    />
                    <span>
                        Portfolio Boost is <strong>{portfolioBoostEnabled ? 'enabled' : 'disabled'}</strong>
                    </span>
                </div>
                <Switch checked={portfolioBoostEnabled} onCheckedChange={setPortfolioBoost} />
            </div>

            <ul className="divide-cocoa-200 divide-y">
                {MANAGEMENT_FLAGS.map((flag) => {
                    const Icon = flag.icon;
                    return (
                        <li key={flag.title} className="flex items-start gap-4 py-4">
                            <div className="bg-cocoa-50 text-cocoa-700 flex h-10 w-10 shrink-0 items-center justify-center rounded-md">
                                <Icon className="h-5 w-5" />
                            </div>
                            <div className="min-w-0 flex-1">
                                <div className="text-cocoa-1000 text-sm font-medium">
                                    {flag.title}
                                    {flag.requiresTaxable && <span className="text-cocoa-500"> *</span>}
                                </div>
                                <p className="text-cocoa-600 mt-0.5 text-sm">{flag.description}</p>
                            </div>
                            {flag.toggleKey && (
                                <Switch
                                    checked={management[flag.toggleKey]}
                                    disabled={!portfolioBoostEnabled}
                                    onCheckedChange={(value) => setFlag(flag.toggleKey!, value)}
                                />
                            )}
                        </li>
                    );
                })}
            </ul>

            <p className="text-cocoa-500 text-right text-xs italic">{TAXABLE_FOOTNOTE}</p>
        </div>
    );
};
